<script setup>
import ActionButton from "@/components/utils/ActionButton.vue";
import Table from "@/components/utils/Table.vue";
import { actions } from "@/utils/constants";

const props = defineProps({
  properties: {
    type: Array,
    default: null,
  },
  propertiesNotEmpty: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["viewPropertyClicked"]);

function formatNumber(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(date) {
  if (!date) return "—";
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1
  ).padStart(2, "0")}/${d.getFullYear()}`;
}
</script>

<template>
  <Table
    :headers="[
      { text: 'PN', value: 'pn' },
      { text: 'LR Number', value: 'lr_number' },
      { text: 'Title PN', value: 'title_deed_pn' },
      { text: 'Sub County', value: 'sub_county' },
      { text: 'Ward', value: 'ward' },
      { text: 'Size (m²)', value: 'size_sqm' },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Document', value: 'document', sortable: false },
      { text: 'Created', value: 'created_at' },
      { text: 'View', value: 'view', action: true },
    ]"
    :items="properties"
    :loading="loading"
    emptyText="No scheme plan properties to show"
    headClasses="primary white--text"
  >
    <template #item.title_deed_pn="{ item: property }">
      {{ property.title_deed_pn || "—" }}
    </template>

    <template #item.sub_county="{ item: property }">
      {{
        property.sub_county ? property.sub_county.sub_county_name : "—"
      }}
    </template>

    <template #item.ward="{ item: property }">
      {{ property.ward ? property.ward.ward_name : "—" }}
    </template>

    <template #item.size_sqm="{ item: property }">
      {{ formatNumber(property.size_sqm) }}
    </template>

    <template #item.status="{ item: property }">
      <v-chip
        x-small
        :color="property.workflow_status && property.workflow_status.status_name === 'Active' ? 'success' : 'warning'"
        dark
        class="font-weight-bold"
      >
        <v-icon x-small left>
          {{ property.workflow_status && property.workflow_status.status_name === 'Active' ? 'mdi-check-circle' : 'mdi-clock-outline' }}
        </v-icon>
        {{ property.workflow_status ? property.workflow_status.status_name : '—' }}
      </v-chip>
    </template>

    <template #item.document="{ item: property }">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            :color="property.title_deed_scan_url ? 'success' : 'grey lighten-1'"
            small v-bind="attrs" v-on="on"
          >
            {{
              property.title_deed_scan_url
                ? "mdi-file-check"
                : "mdi-file-remove-outline"
            }}
          </v-icon>
        </template>
        <span>{{ property.title_deed_scan_url ? 'Title Deed Present' : 'Title Deed Absent' }}</span>
      </v-tooltip>
    </template>

    <template #item.created_at="{ item: property }">
      {{ formatDate(property.created_at) }}
    </template>

    <template #item.view="{ item: property }">
      <ActionButton
        :action="actions.view"
        inTable
        @click="$emit('viewPropertyClicked', property)"
      />
    </template>
  </Table>
</template>
